import { RPC_ENDPOINT } from "@solomon-apps/config";
import { ConnectionManager } from "@solomon-labs/solana-connection";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes } from "react-router-dom";

import { PageviewTracker } from "./analytics/PageviewTracker";
import { initAnalytics } from "./analytics/posthog";
import { initSentry } from "./analytics/sentry";
import { AgentationToolbar } from "./components/AgentationToolbar";
import { AppErrorFallback } from "./components/AppErrorFallback";
import { ErrorBoundary } from "./components/Layout/ErrorBoundary";
import { NotFound } from "./pages/NotFound";
import type { AppConfig } from "./types/app";
import { WalletProvider } from "./wallet";

// Create a QueryClient instance for TanStack React Query
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes
            refetchOnWindowFocus: false,
            retry: 2
        }
    }
});

export async function createApp({
    appName,
    rootSelector = "#root",
    routes,
    components: { v3Layout: V3LayoutComponent } = {},
    pages: { notFound: NotFoundPage = NotFound } = {},
    setup
}: AppConfig): Promise<void> {
    // Initialize Sentry error monitoring and tracing
    initSentry({ appName });

    // Initialize ConnectionManager before anything else
    ConnectionManager.setEndpoint(RPC_ENDPOINT);

    // Initialize PostHog analytics (production only)
    initAnalytics();

    // Note: We intentionally skip CacheManager.init() - the caching package has
    // serialization bugs with BigInt/PublicKey that cause more problems than it solves.
    // The solomon-labs packages will work without caching, just with fresh RPC calls.

    if (setup) {
        await setup();
    }

    function RouterImpl(): React.ReactElement {
        const routeElements = routes.map((route) => (
            <Route
                key={route.key}
                path={route.path}
                element={<route.component />}
            />
        ));

        const catchAll = (
            <Route
                path="*"
                element={<NotFoundPage key="NotFound" />}
                key={"CatchAll"}
            />
        );

        return (
            <BrowserRouter>
                <PageviewTracker />
                <Routes>
                    {V3LayoutComponent ? (
                        <Route element={<V3LayoutComponent key="V3Layout" />}>
                            {routeElements}
                            {catchAll}
                        </Route>
                    ) : (
                        <>
                            {routeElements}
                            {catchAll}
                        </>
                    )}
                </Routes>
            </BrowserRouter>
        );
    }

    const Router = React.memo(RouterImpl);

    function App(): React.ReactElement {
        return (
            <QueryClientProvider client={queryClient}>
                <WalletProvider>
                    <Router />
                    <AgentationToolbar />
                </WalletProvider>
            </QueryClientProvider>
        );
    }

    const rootElement = document.querySelector(rootSelector);
    if (!rootElement) {
        throw new Error("Root element not found");
    }

    const root = createRoot(rootElement);
    root.render(
        <ErrorBoundary fallback={AppErrorFallback}>
            <App />
        </ErrorBoundary>
    );
}
